import React, { useState, useEffect } from 'react';
import { dataService, DataFile } from '../services/dataService';
import '../styles/dashboard-pages.css';

const DataManagementPage: React.FC = () => {
  const [dataFiles, setDataFiles] = useState<DataFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadDataFiles();
  }, []);

  const loadDataFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const files = await dataService.listData();
      console.log('[DataManagementPage] Loaded data files:', files);
      setDataFiles(files);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to load data files';
      setError(errorMsg);
      console.error('[DataManagementPage] Error loading data files:', err);
      alert(`Error loading data files: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const filteredDataFiles = dataFiles.filter((file) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return file.name.toLowerCase().includes(query);
  });

  return (
    <div className="dashboard-page">
      <div className="page-actions">
        <button className="action-btn primary">
          + Upload Data
        </button>
        <button className="action-btn secondary">
          📊 Import from URL
        </button>
      </div>

      <div className="page-sections">
        <section className="section-card">
          <h2>Storage Overview</h2>
          <div className="storage-stats">
            <div className="storage-card">
              <div className="storage-label">Total Storage Used</div>
              <div className="storage-value">290 MB</div>
              <div className="storage-bar">
                <div className="storage-fill" style={{ width: '58%' }}></div>
              </div>
              <div className="storage-limit">500 MB limit</div>
            </div>
            <div className="storage-card">
              <div className="storage-label">Number of Data Files</div>
              <div className="storage-value">3</div>
              <div className="storage-detail">Unlimited data files</div>
            </div>
            <div className="storage-card">
              <div className="storage-label">Total Rows</div>
              <div className="storage-value">130K</div>
              <div className="storage-detail">Across all data</div>
            </div>
          </div>
        </section>

        <section className="section-card">
          <div className="section-header">
            <h2>Your Data</h2>
            <input
              type="text"
              placeholder="Search data..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="empty-state">
              <div className="empty-icon">⏳</div>
              <p>Loading data files...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <p>Error: {error}</p>
              <button className="action-btn secondary" onClick={loadDataFiles} style={{ marginTop: '12px' }}>
                Retry
              </button>
            </div>
          ) : filteredDataFiles.length > 0 ? (
            <div className="datasets-table">
              <div className="table-header">
                <div className="col-name">Data Name</div>
                <div className="col-type">Type</div>
                <div className="col-size">Samples</div>
                <div className="col-rows">Created</div>
                <div className="col-status">Status</div>
                <div className="col-actions">Actions</div>
              </div>

              {filteredDataFiles.map((file) => (
                <div key={file.id} className="table-row">
                  <div className="col-name">{file.name}</div>
                  <div className="col-type">{file.data_type}</div>
                  <div className="col-size">{file.sample_count ? file.sample_count.toLocaleString() : 'N/A'}</div>
                  <div className="col-rows">{formatDate(file.created_at)}</div>
                  <div className="col-status">
                    <span className="status-badge ready">Ready</span>
                  </div>
                  <div className="col-actions">
                    <button className="icon-btn" title="Configure as Dataset">⚙️</button>
                    <button className="icon-btn" title="Preview">👁️</button>
                    <button className="icon-btn" title="Download">📥</button>
                    <button className="icon-btn" title="Share">🔗</button>
                    <button 
                      className="icon-btn danger" 
                      title="Delete"
                      onClick={async () => {
                        if (window.confirm(`Delete ${file.name}?`)) {
                          try {
                            await dataService.deleteData(file.id);
                            loadDataFiles();
                          } catch (err: any) {
                            alert(`Failed to delete: ${err.message}`);
                          }
                        }
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📁</div>
              <p>
                {searchQuery 
                  ? 'No data files match your search.' 
                  : 'No data files yet. Upload your first data file to get started.'}
              </p>
            </div>
          )}
        </section>

        <section className="section-card">
          <h2>Data Processing</h2>
          <div className="processing-options">
            <div className="process-card">
              <div className="process-icon">⚙️</div>
              <div className="process-name">Data Cleaning</div>
              <div className="process-desc">Remove duplicates and handle missing values</div>
              <button className="action-btn secondary small">Start</button>
            </div>
            <div className="process-card">
              <div className="process-icon">📊</div>
              <div className="process-name">Data Normalization</div>
              <div className="process-desc">Scale and normalize your data</div>
              <button className="action-btn secondary small">Start</button>
            </div>
            <div className="process-card">
              <div className="process-icon">🔄</div>
              <div className="process-name">Data Augmentation</div>
              <div className="process-desc">Generate synthetic data variations</div>
              <button className="action-btn secondary small">Start</button>
            </div>
            <div className="process-card">
              <div className="process-icon">✂️</div>
              <div className="process-name">Data Splitting</div>
              <div className="process-desc">Split into train/test/validation sets</div>
              <button className="action-btn secondary small">Start</button>
            </div>
          </div>
        </section>

        <section className="section-card">
          <h2>Upload Data</h2>
          <div className="upload-area">
            <div className="upload-icon">📤</div>
            <div className="upload-text">
              <div className="upload-main">Drag and drop your files here</div>
              <div className="upload-sub">or click to select files</div>
            </div>
            <div className="upload-formats">
              <small>Supported formats: CSV, JSON, Parquet, Excel, HDF5</small>
            </div>
            <button className="action-btn primary">Select Files</button>
          </div>
        </section>

        <section className="section-card">
          <h2>Public Data Sources</h2>
          <div className="public-datasets">
            <div className="dataset-card">
              <div className="ds-icon">🖼️</div>
              <div className="ds-name">ImageNet</div>
              <div className="ds-desc">14 million images, 1000 categories</div>
              <button className="action-btn secondary small">Import</button>
            </div>
            <div className="dataset-card">
              <div className="ds-icon">📝</div>
              <div className="ds-name">Wikipedia</div>
              <div className="ds-desc">Text corpus with 6 billion words</div>
              <button className="action-btn secondary small">Import</button>
            </div>
            <div className="dataset-card">
              <div className="ds-icon">🎬</div>
              <div className="ds-name">MovieLens</div>
              <div className="ds-desc">Movie ratings and metadata</div>
              <button className="action-btn secondary small">Import</button>
            </div>
            <div className="dataset-card">
              <div className="ds-icon">💬</div>
              <div className="ds-name">Common Crawl</div>
              <div className="ds-desc">Web page snapshots and data</div>
              <button className="action-btn secondary small">Import</button>
            </div>
          </div>
        </section>

        <section className="section-card">
          <h2>Data Statistics</h2>
          <div className="stats-overview">
            <div className="stat-box">
              <div className="stat-title">Average Data Size</div>
              <div className="stat-big">97 MB</div>
            </div>
            <div className="stat-box">
              <div className="stat-title">Total Rows Processed</div>
              <div className="stat-big">130K</div>
            </div>
            <div className="stat-box">
              <div className="stat-title">Data Cleaning Tasks</div>
              <div className="stat-big">12</div>
            </div>
            <div className="stat-box">
              <div className="stat-title">Last Upload</div>
              <div className="stat-big">Nov 8</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DataManagementPage;

